/**
 * Memoize a value derived from an item, keyed by the item's identity.
 *
 * Sort comparators are why this exists. A sort runs its comparator ~n·log2(n)
 * times — ~178,000 for the 13,000-row order-edit table — but there are only n
 * distinct keys, and the accessors producing them are derived values
 * (reorderLabel, formatUnitsQuantity, four-level reaches into oi.product) far
 * more expensive than the comparison they feed. Caching per item turns hundreds
 * of thousands of accessor calls per sort into one per row.
 *
 * Entries are held weakly, so an item dropping out of the data takes its value
 * with it. The cache lives exactly as long as the returned function, so it can
 * never outlive the `derive` closure that filled it.
 *
 * Items are assumed immutable — a changed item arrives as a new object. That is
 * already what the sorted React lists assume about this data.
 *
 * `V` excludes null and undefined deliberately: the cache probes with a single
 * WeakMap read, so a nullish value would silently recompute on every call.
 */
export function cacheByItem<T extends object, V extends NonNullable<unknown>>(
  derive: (item: T) => V
): (item: T) => V {
  const cache = new WeakMap<T, V>();

  return (item) => {
    const cached = cache.get(item);
    if (cached !== undefined) {
      return cached;
    }

    const value = derive(item);
    cache.set(item, value);

    return value;
  };
}
